/**
 * DecorativeFrame - Cadre décoratif avec coins ornementaux style livre de jeu
 * 
 * SYSTÈME DE COORDONNÉES (Source de vérité unique) :
 * ┌────────────────────────────────────────┐
 * │ CORNER_OFFSET = 10px                   │
 * │ CORNER_SIZE = 60px                     │
 * │ BORDER_THICKNESS = 2px                 │
 * │                                        │
 * │ Zone de référence :                    │
 * │   top: CORNER_OFFSET + topOffset       │
 * │   left: CORNER_OFFSET                  │
 * │   right: CORNER_OFFSET                 │
 * │   bottom: CORNER_OFFSET + bottomOffset │
 * │                                        │
 * │ Les bordures commencent à :            │
 * │   CORNER_OFFSET + CORNER_SIZE          │
 * └────────────────────────────────────────┘
 */

import React, {useMemo} from 'react';
import {View, StyleSheet, Platform} from 'react-native';
import Svg, {Path, Defs, LinearGradient, Stop} from 'react-native-svg';
import {useTheme} from '../contexts/ThemeContext';

// ═══════════════════════════════════════════════════════════════
// CONSTANTES GÉOMÉTRIQUES - SOURCE DE VÉRITÉ UNIQUE
// ═══════════════════════════════════════════════════════════════
const CORNER_OFFSET = 10;      // Distance depuis les bords du container
const CORNER_SIZE = 60;        // Taille des ornements SVG (width & height)
const BORDER_THICKNESS = 2;    // Épaisseur des lignes
const BORDER_START = CORNER_OFFSET + CORNER_SIZE; // Les bordures commencent où finissent les coins

interface DecorativeFrameProps {
  topOffset?: number;    // Offset en pixels pour descendre le cadre (ex: pour header)
  bottomOffset?: number; // Offset en pixels pour remonter le cadre (ex: pour footer)
}

const DecorativeFrame: React.FC<DecorativeFrameProps> = ({topOffset = 0, bottomOffset = 0}) => {
  const {isDarkMode} = useTheme();
  
  // Couleurs pour les ornements
  const ornamentColor = isDarkMode ? '#6B5A45' : '#8B7355';
  const ornamentHighlight = isDarkMode ? '#8B7355' : '#A68968';
  const ornamentShadow = isDarkMode ? '#4A3F32' : '#6B5A45';
  
  // Calcul des positions une seule fois avec les offsets
  const positions = useMemo(() => ({
    corners: {
      top: CORNER_OFFSET + topOffset,
      bottom: CORNER_OFFSET + bottomOffset,
      left: CORNER_OFFSET,
      right: CORNER_OFFSET,
    },
    borders: {
      top: CORNER_OFFSET + topOffset,
      bottom: CORNER_OFFSET + bottomOffset,
      left: CORNER_OFFSET,
      right: CORNER_OFFSET,
      horizontalStart: BORDER_START,
      verticalStart: BORDER_START + topOffset,
      verticalEnd: BORDER_START + bottomOffset,
    },
  }), [topOffset, bottomOffset]);
  
  return (
    <View style={styles.container}>
      {/* ═══════ COINS ORNEMENTAUX ═══════ */}
      
      {/* Coin supérieur gauche */}
      <View style={[
        styles.corner,
        {
          top: positions.corners.top,
          left: positions.corners.left,
        }
      ]}>
        <Svg width={CORNER_SIZE} height={CORNER_SIZE} viewBox={`0 0 ${CORNER_SIZE} ${CORNER_SIZE}`}>
          <Defs>
            <LinearGradient id="ornamentGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <Stop offset="0%" stopColor={ornamentHighlight} />
              <Stop offset="50%" stopColor={ornamentColor} />
              <Stop offset="100%" stopColor={ornamentShadow} />
            </LinearGradient>
          </Defs>
          <Path
            d="M 5 5 Q 8 5 10 8 L 15 13 Q 18 16 20 16 Q 22 16 22 14 L 22 10 Q 22 8 24 8 L 28 8 Q 30 8 30 6 L 30 5 M 5 5 Q 5 8 8 10 L 13 15 Q 16 18 16 20 Q 16 22 14 22 L 10 22 Q 8 22 8 24 L 8 28 Q 8 30 6 30 L 5 30"
            fill="url(#ornamentGrad)"
            stroke={ornamentShadow}
            strokeWidth="0.5"
          />
          <Path
            d="M 10 5 Q 12 7 14 9 M 5 10 Q 7 12 9 14 M 18 10 Q 20 12 22 14 M 10 18 Q 12 20 14 22"
            fill="none"
            stroke={ornamentHighlight}
            strokeWidth="1"
            strokeLinecap="round"
          />
        </Svg>
      </View>
      
      {/* Coin supérieur droit */}
      <View style={[
        styles.corner,
        {
          top: positions.corners.top,
          right: positions.corners.right,
        }
      ]}>
        <Svg width={CORNER_SIZE} height={CORNER_SIZE} viewBox={`0 0 ${CORNER_SIZE} ${CORNER_SIZE}`} style={{transform: [{scaleX: -1}]}}>
          <Defs>
            <LinearGradient id="ornamentGradR" x1="0%" y1="0%" x2="100%" y2="100%">
              <Stop offset="0%" stopColor={ornamentHighlight} />
              <Stop offset="50%" stopColor={ornamentColor} />
              <Stop offset="100%" stopColor={ornamentShadow} />
            </LinearGradient>
          </Defs>
          <Path
            d="M 5 5 Q 8 5 10 8 L 15 13 Q 18 16 20 16 Q 22 16 22 14 L 22 10 Q 22 8 24 8 L 28 8 Q 30 8 30 6 L 30 5 M 5 5 Q 5 8 8 10 L 13 15 Q 16 18 16 20 Q 16 22 14 22 L 10 22 Q 8 22 8 24 L 8 28 Q 8 30 6 30 L 5 30"
            fill="url(#ornamentGradR)"
            stroke={ornamentShadow}
            strokeWidth="0.5"
          />
          <Path
            d="M 10 5 Q 12 7 14 9 M 5 10 Q 7 12 9 14 M 18 10 Q 20 12 22 14 M 10 18 Q 12 20 14 22"
            fill="none"
            stroke={ornamentHighlight}
            strokeWidth="1"
            strokeLinecap="round"
          />
        </Svg>
      </View>
      
      {/* Coin inférieur gauche */}
      <View style={[
        styles.corner,
        {
          bottom: positions.corners.bottom,
          left: positions.corners.left,
        }
      ]}>
        <Svg width={CORNER_SIZE} height={CORNER_SIZE} viewBox={`0 0 ${CORNER_SIZE} ${CORNER_SIZE}`} style={{transform: [{scaleY: -1}]}}>
          <Defs>
            <LinearGradient id="ornamentGradBL" x1="0%" y1="0%" x2="100%" y2="100%">
              <Stop offset="0%" stopColor={ornamentHighlight} />
              <Stop offset="50%" stopColor={ornamentColor} />
              <Stop offset="100%" stopColor={ornamentShadow} />
            </LinearGradient>
          </Defs>
          <Path
            d="M 5 5 Q 8 5 10 8 L 15 13 Q 18 16 20 16 Q 22 16 22 14 L 22 10 Q 22 8 24 8 L 28 8 Q 30 8 30 6 L 30 5 M 5 5 Q 5 8 8 10 L 13 15 Q 16 18 16 20 Q 16 22 14 22 L 10 22 Q 8 22 8 24 L 8 28 Q 8 30 6 30 L 5 30"
            fill="url(#ornamentGradBL)"
            stroke={ornamentShadow}
            strokeWidth="0.5"
          />
          <Path
            d="M 10 5 Q 12 7 14 9 M 5 10 Q 7 12 9 14 M 18 10 Q 20 12 22 14 M 10 18 Q 12 20 14 22"
            fill="none"
            stroke={ornamentHighlight}
            strokeWidth="1"
            strokeLinecap="round"
          />
        </Svg>
      </View>
      
      {/* Coin inférieur droit */}
      <View style={[
        styles.corner,
        {
          bottom: positions.corners.bottom,
          right: positions.corners.right,
        }
      ]}>
        <Svg width={CORNER_SIZE} height={CORNER_SIZE} viewBox={`0 0 ${CORNER_SIZE} ${CORNER_SIZE}`} style={{transform: [{scaleX: -1}, {scaleY: -1}]}}>
          <Defs>
            <LinearGradient id="ornamentGradBR" x1="0%" y1="0%" x2="100%" y2="100%">
              <Stop offset="0%" stopColor={ornamentHighlight} />
              <Stop offset="50%" stopColor={ornamentColor} />
              <Stop offset="100%" stopColor={ornamentShadow} />
            </LinearGradient>
          </Defs>
          <Path
            d="M 5 5 Q 8 5 10 8 L 15 13 Q 18 16 20 16 Q 22 16 22 14 L 22 10 Q 22 8 24 8 L 28 8 Q 30 8 30 6 L 30 5 M 5 5 Q 5 8 8 10 L 13 15 Q 16 18 16 20 Q 16 22 14 22 L 10 22 Q 8 22 8 24 L 8 28 Q 8 30 6 30 L 5 30"
            fill="url(#ornamentGradBR)"
            stroke={ornamentShadow}
            strokeWidth="0.5"
          />
          <Path
            d="M 10 5 Q 12 7 14 9 M 5 10 Q 7 12 9 14 M 18 10 Q 20 12 22 14 M 10 18 Q 12 20 14 22"
            fill="none"
            stroke={ornamentHighlight}
            strokeWidth="1"
            strokeLinecap="round"
          />
        </Svg>
      </View>
      
      {/* ═══════ BORDURES (commencent exactement où finissent les coins) ═══════ */}
      
      {/* Bordure supérieure */}
      <View style={[
        styles.border,
        {
          position: 'absolute',
          top: positions.borders.top,
          left: positions.borders.horizontalStart,
          right: positions.borders.horizontalStart,
          height: BORDER_THICKNESS,
          backgroundColor: ornamentColor,
          ...Platform.select({
            web: {
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
            },
          }),
        }
      ]} />
      
      {/* Bordure inférieure */}
      <View style={[
        styles.border,
        {
          position: 'absolute',
          bottom: positions.borders.bottom,
          left: positions.borders.horizontalStart,
          right: positions.borders.horizontalStart,
          height: BORDER_THICKNESS,
          backgroundColor: ornamentColor,
          ...Platform.select({
            web: {
              boxShadow: '0 -1px 2px rgba(0, 0, 0, 0.2)',
            },
          }),
        }
      ]} />
      
      {/* Bordure gauche */}
      <View style={[
        styles.border,
        {
          position: 'absolute',
          left: positions.borders.left,
          top: positions.borders.verticalStart,
          bottom: positions.borders.verticalEnd,
          width: BORDER_THICKNESS,
          backgroundColor: ornamentColor,
          ...Platform.select({
            web: {
              boxShadow: '1px 0 2px rgba(0, 0, 0, 0.2)',
            },
          }),
        }
      ]} />
      
      {/* Bordure droite */}
      <View style={[
        styles.border,
        {
          position: 'absolute',
          right: positions.borders.right,
          top: positions.borders.verticalStart,
          bottom: positions.borders.verticalEnd,
          width: BORDER_THICKNESS,
          backgroundColor: ornamentColor,
          ...Platform.select({
            web: {
              boxShadow: '-1px 0 2px rgba(0, 0, 0, 0.2)',
            },
          }),
        }
      ]} />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    pointerEvents: 'none', // Ne bloque pas les interactions
    zIndex: 1, // Au-dessus de la texture mais en dessous du contenu
    opacity: 0.7, // Un peu transparent
  },
  corner: {
    position: 'absolute',
    zIndex: 2,
    width: CORNER_SIZE,
    height: CORNER_SIZE,
  },
  border: {
    zIndex: 1,
  },
});

export default DecorativeFrame;
